/**
 * Presentacion de un valor sin edicion.
 *
 * Es lo que dibuja cada celda de la lista, cada linea de una tarjeta de kanban y
 * cada campo bloqueado del formulario. Que sea UN componente es lo que garantiza
 * que un importe se vea igual en la tabla, en la tarjeta y en el formulario.
 */

import clsx from 'clsx';

import { PALETA_TABLERO } from '@/components/fields/ColorField';
import {
  displayText,
  formatHours,
  formatMonetary,
  formatNumber,
  formatPercent,
  formatUnit,
  isMany2One,
  toIdList,
} from '@/components/fields/format';
import { READONLY_CLASS } from '@/components/fields/controls';
import type { FieldDisplayProps } from '@/components/fields/types';
import { IconCheck, IconClose } from '@/components/shell/icons';
import { Badge } from '@/components/shell/primitives';
import type { FieldMeta, JsonValue } from '@/lib/types';

type Tono = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

const TONOS_VALIDOS = new Set<Tono>(['neutral', 'accent', 'success', 'warning', 'danger']);

/**
 * Semaforo por valor de estado (ARQUITECTURA.md §7).
 *
 * Primero se respeta lo que declaro el modulo en `options.tones`; recien si no
 * hay declaracion se aplica una heuristica sobre los codigos habituales. La
 * heuristica es deliberadamente conservadora: ante la duda, neutro. Pintar de
 * verde un estado que no lo es seria peor que no pintarlo.
 */
const HEURISTICA: Array<[RegExp, Tono]> = [
  // `closed` e `issued` son los gemelos ingleses de `cerrad` y `enviad`, que ya
  // estaban: sin ellos, un circuito con codigos en ingles — que es lo normal en
  // los modelos — sale entero en gris y deja de ser un semaforo.
  [/^(done|paid|posted|confirmed|active|approved|ok|closed|pagad|confirmad|aprobad|cerrad)/i, 'success'],
  [/^(cancel|rejected|error|failed|anulad|rechazad|vencid|impag)/i, 'danger'],
  [/^(late|pending|waiting|review|overdue|pendiente|revisi|espera|parcial)/i, 'warning'],
  [/^(open|in_progress|running|sent|issued|abiert|proceso|enviad)/i, 'accent'],
];

export function stateTone(meta: FieldMeta, valor: JsonValue): Tono {
  const declarados = meta.options?.tones;
  if (declarados !== null && typeof declarados === 'object' && !Array.isArray(declarados)) {
    const propuesto = (declarados as Record<string, JsonValue>)[String(valor)];
    if (typeof propuesto === 'string' && TONOS_VALIDOS.has(propuesto as Tono)) {
      return propuesto as Tono;
    }
  }
  const texto = String(valor ?? '');
  for (const [patron, tono] of HEURISTICA) {
    if (patron.test(texto)) return tono;
  }
  return 'neutral';
}

/**
 * Campo bloqueado dentro de un formulario.
 *
 * Es un `<output>` y no un `<input disabled>` a proposito: `output` es un
 * elemento etiquetable, asi que la `<label>` del campo lo sigue nombrando para
 * un lector de pantalla, el texto se puede seleccionar y copiar, y no aparece un
 * control que invita a escribir donde no se puede.
 */
export function ReadonlyOutput({
  id,
  meta,
  value,
  describedBy,
}: {
  id: string;
  meta: FieldMeta;
  value: FieldDisplayProps['value'];
  describedBy?: string;
}) {
  return (
    <output id={id} className={READONLY_CLASS} {...(describedBy === undefined ? {} : { 'aria-describedby': describedBy })}>
      <FieldDisplay meta={meta} value={value} />
    </output>
  );
}

export function FieldDisplay({ meta, value, compact = false }: FieldDisplayProps) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-fg-faint">—</span>;
  }

  if (meta.type === 'boolean') {
    return (
      <span className="inline-flex items-center gap-1.5">
        {value === true ? (
          <IconCheck className="h-4 w-4 text-success" />
        ) : (
          <IconClose className="h-4 w-4 text-fg-faint" />
        )}
        <span className={compact ? 'sr-only' : 'text-sm'}>{value === true ? 'Sí' : 'No'}</span>
      </span>
    );
  }

  const widget = meta.widget;

  // Solo lo declarado como `badge` o `status` se pinta como estado. Un campo con
  // `choices` no es necesariamente un estado (un tipo de documento no lo es), y
  // llenar la tabla de pastillas de colores le saca peso a las que sí importan.
  //
  // `status` se dibuja acá como pastilla y no como la barra de etapas completa:
  // la barra es para la cabecera de un formulario, donde hay un registro y
  // espacio; dentro de una celda de tabla sería ilegible.
  if (widget === 'badge' || widget === 'status') {
    const etiqueta = displayText(meta, value);
    if (etiqueta === '') return <span className="text-fg-faint">—</span>;
    // Ancho acotado + truncado: una etiqueta larga («Factura de cliente») no
    // ensancha ni parte la columna; el tooltip conserva el texto completo.
    return (
      <Badge tone={stateTone(meta, value as JsonValue)} title={etiqueta} className={compact ? 'max-w-[14rem]' : undefined}>
        <span className="min-w-0 truncate">{etiqueta}</span>
      </Badge>
    );
  }

  // Una clave guardada NO se imprime nunca, ni en la ficha bloqueada ni en una
  // celda de la lista. Esta rama es la otra mitad de `PasswordField`: sin ella
  // el widget dibujaria el formulario enmascarado y la COLUMNA de la lista
  // mostraria la clave en texto plano, porque `ReadonlyOutput` delega acá y lo
  // que no tiene rama propia cae al valor crudo unas lineas mas abajo.
  //
  // Se dibuja una cantidad FIJA de puntos, no `value.length`: el largo de una
  // clave tambien es informacion sobre la clave.
  if (widget === 'password') {
    return (
      <span className="font-mono tracking-widest text-fg-muted" title="Clave guardada">
        ••••••••
      </span>
    );
  }

  if (widget === 'money' || widget === 'monetary') {
    return <span className="tabular-nums">{formatMonetary(value, meta)}</span>;
  }

  if (widget === 'percent') {
    return <span className="tabular-nums">{formatPercent(value, meta)}</span>;
  }

  if (widget === 'area') {
    return <span className="tabular-nums">{formatUnit(value, meta)}</span>;
  }

  if (widget === 'hours') {
    return <span className="tabular-nums">{formatHours(value)}</span>;
  }

  if (widget === 'color') {
    // Dos formas de declarar lo mismo: indice de paleta (`integer`) o `#RRGGBB`
    // (`char`). Ver `ColorField`, que edita las dos.
    const color =
      meta.type === 'integer'
        ? (PALETA_TABLERO[typeof value === 'number' ? value : 0] ?? PALETA_TABLERO[0])
        : typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value)
          ? value
          : null;
    if (color === null) return <span className="text-fg-faint">—</span>;
    return (
      <span className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="h-3.5 w-3.5 shrink-0 rounded-full border border-line"
          style={{ backgroundColor: color }}
        />
        <span className={compact ? 'sr-only' : 'font-mono text-xs text-fg-muted'}>
          {meta.type === 'integer' ? `Color ${String(value ?? 0)}` : String(value)}
        </span>
      </span>
    );
  }

  // `priority` es una escala corta declarada con `choices` ("Baja/Normal/Alta/
  // Urgente"): se pinta como pastilla para que se lea de un vistazo en la tabla,
  // con el tono creciendo con la urgencia.
  if (widget === 'priority') {
    const etiqueta = displayText(meta, value);
    if (etiqueta === '') return <span className="text-fg-faint">—</span>;
    const nivel = Number(value);
    const tono: Tono = nivel >= 3 ? 'danger' : nivel === 2 ? 'warning' : nivel === 1 ? 'neutral' : 'neutral';
    return <Badge tone={tono}>{etiqueta}</Badge>;
  }

  if (widget === 'progress') {
    const numero = Number(value);
    const porcentaje = Number.isFinite(numero) ? Math.max(0, Math.min(100, numero)) : 0;
    return (
      <span className="flex items-center gap-2">
        <span
          role="progressbar"
          aria-valuenow={Math.round(porcentaje)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={meta.label}
          className="h-1.5 w-20 overflow-hidden rounded-full bg-surface-raised"
        >
          <span className="block h-full rounded-full bg-accent" style={{ width: `${porcentaje}%` }} />
        </span>
        <span className="text-xs tabular-nums text-fg-muted">{Math.round(porcentaje)}%</span>
      </span>
    );
  }

  if (meta.type === 'many2one') {
    return <span className={compact ? 'truncate' : ''}>{isMany2One(value) ? value.display_name : String(value)}</span>;
  }

  if (meta.type === 'one2many' || meta.type === 'many2many') {
    const cantidad = toIdList(value).length;
    if (cantidad === 0) return <span className="text-fg-faint">—</span>;
    return <Badge>{cantidad === 1 ? '1 elemento' : `${cantidad} elementos`}</Badge>;
  }

  if (meta.type === 'email') {
    return (
      <a href={`mailto:${String(value)}`} className="text-accent hover:underline">
        {String(value)}
      </a>
    );
  }

  if (meta.type === 'url') {
    return (
      <a
        href={String(value)}
        target="_blank"
        rel="noreferrer noopener"
        className="text-accent hover:underline"
      >
        {String(value)}
      </a>
    );
  }

  if (meta.type === 'integer' || meta.type === 'float' || meta.type === 'decimal') {
    return <span className="tabular-nums">{formatNumber(value, meta)}</span>;
  }

  return (
    <span className={clsx(compact && 'block truncate', meta.type === 'text' && !compact && 'whitespace-pre-line')}>
      {displayText(meta, value)}
    </span>
  );
}
